import React, { useState, useEffect, useCallback } from 'react';
import {
  PlayIcon,
  BookOpenIcon,
  DocumentTextIcon,
  BeakerIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  ArrowLeftIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import SubjectInteractiveRouter from './SubjectInteractiveRouter';

interface ContentViewerProps {
  content: any;
  courseId: string;
  moduleId: string;
  isCompleted: boolean;
  onComplete: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  canNavigateNext?: boolean;
  canNavigatePrevious?: boolean;
}

interface ViewingSession {
  startTime: Date;
  contentId: string;
  totalTime: number;
  interactions: string[];
}

const ContentViewer: React.FC<ContentViewerProps> = ({
  content,
  courseId,
  moduleId,
  isCompleted,
  onComplete,
  onNext,
  onPrevious,
  canNavigateNext = false,
  canNavigatePrevious = false
}) => {
  const [viewingSession, setViewingSession] = useState<ViewingSession | null>(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [minimumTimeReached, setMinimumTimeReached] = useState(false);
  const [interactiveAnswerCorrect, setInteractiveAnswerCorrect] = useState<boolean | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [actualVideoDuration, setActualVideoDuration] = useState<number | null>(null);

  // Minimum viewing time based on content type (in seconds)
  const getMinimumViewingTime = () => {
    const duration = typeof content.duration === 'string' ? parseInt(content.duration) || 0 : (content.duration || 0);
    switch (content.type) {
      case 'video':
        // Use 95% of actual video duration when available, otherwise fallback to 80% of database duration
        if (actualVideoDuration !== null) {
          return Math.max(actualVideoDuration * 60 * 0.95, 30); // 95% of actual video duration, minimum 30 seconds
        }
        return Math.max(duration * 60 * 0.8, 30); // 80% of database duration, minimum 30 seconds
      case 'text':
        return Math.max(duration * 60 * 0.6, 60); // 60% of estimated reading time, minimum 1 minute
      case 'interactive':
        return Math.max(duration * 60 * 0.7, 45); // 70% of estimated time
      case 'finance-interactive':
        return Math.max(duration * 60 * 0.7, 60); // 70% of estimated time, minimum 1 minute
      case 'document':
        return 30; // Minimum 30 seconds for documents
      default:
        return 30;
    }
  };

  const minimumTime = getMinimumViewingTime();

  // Check if minimum time is reached when actualVideoDuration changes
  useEffect(() => {
    if (content.type === 'video' && actualVideoDuration !== null && timeSpent >= minimumTime && !minimumTimeReached && viewingSession) {
      setMinimumTimeReached(true);
      setViewingSession(prev => {
        if (!prev) return null;
        return {
          ...prev,
          interactions: [...prev.interactions, `${new Date().toISOString()}: minimum_time_reached_updated`]
        };
      });
    }
  }, [actualVideoDuration, minimumTime, timeSpent, minimumTimeReached, content.type, viewingSession]);

  // Start viewing session for all content types
  useEffect(() => {
    // Scroll to top when content changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Clean up previous session if exists
    if (viewingSession) {
      trackViewingSession(viewingSession);
    }
    
    // Reset all states when content changes
    setTimeSpent(0);
    setMinimumTimeReached(false);
    setVideoCompleted(false);
    setVideoPlaying(false);
    setViewingSession(null);
    setHasScrolledToEnd(false);
    setActualVideoDuration(null);
    
    // Start session immediately for all content types except completed ones
    if (!isCompleted) {
      const session: ViewingSession = {
        startTime: new Date(),
        contentId: content.id,
        totalTime: 0,
        interactions: []
      };
      setViewingSession(session);
      addInteraction(content.type === 'video' ? 'video_content_loaded' : 'content_started');
    }

    // Cleanup on unmount or content change
    return () => {
      // Don't track on every change, only on actual unmount
    };
  }, [content.id]);

  // Add proper session cleanup on component unmount
  useEffect(() => {
    return () => {
      if (viewingSession) {
        trackViewingSession(viewingSession);
      }
    };
  }, []);

  // Track time spent
  useEffect(() => {
    if (!viewingSession || isCompleted) return;

    const interval = setInterval(() => {
      if (isVisible) {
        // Track time for all content types, but with different rules
        let shouldTrackTime = false;
        
        if (content.type === 'video') {
          // For videos: track when playing OR when paused (still engaged)
          // Only don't track if video hasn't been started yet
          shouldTrackTime = videoPlaying || (viewingSession && viewingSession.interactions.some(i => i.includes('video_play_clicked')));
        } else {
          // For non-video content: always track when visible and session exists
          shouldTrackTime = true;
        }
        
        if (shouldTrackTime) {
          setTimeSpent(prev => {
            const newTime = prev + 1;
            
            if (newTime >= minimumTime && !minimumTimeReached) {
              setMinimumTimeReached(true);
              addInteraction('minimum_time_reached');
            }
            
            // Check if video has been fully watched (for videos)
            if (content.type === 'video' && !videoCompleted) {
              const duration = typeof content.duration === 'string' ? parseInt(content.duration) || 0 : (content.duration || 0);
              const videoDurationSeconds = duration * 60;
              // Consider video complete when 90% watched (accounting for pauses)
              if (newTime >= videoDurationSeconds * 0.9) {
                setVideoCompleted(true);
                addInteraction('video_completed');
              }
            }
            
            return newTime;
          });
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [viewingSession, isVisible, minimumTime, minimumTimeReached, isCompleted, content.type, content.duration, videoPlaying, actualVideoDuration]);

  // Track page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
      if (document.hidden) {
        addInteraction('tab_switched_away');
      } else {
        addInteraction('tab_focused');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const addInteraction = useCallback((interaction: string) => {
    // Track answer correctness for interactive exercises
    if (interaction === 'solution_correct') {
      setInteractiveAnswerCorrect(true);
    } else if (interaction === 'solution_incorrect') {
      setInteractiveAnswerCorrect(false);
    }
    
    setViewingSession(prev => {
      if (!prev) return null;
      return {
        ...prev,
        interactions: [...prev.interactions, `${new Date().toISOString()}: ${interaction}`]
      };
    });
  }, []);

  const trackViewingSession = async (session: ViewingSession) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const sessionData = {
        ...session,
        totalTime: timeSpent,
        endTime: new Date(),
        minimumTimeReached,
        hasScrolledToEnd
      };

      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/viewing-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          courseId,
          moduleId,
          contentId: content.id,
          sessionData
        })
      });
    } catch (error) {
      console.error('Error tracking viewing session:', error);
    }
  };

  const handleScrollToEnd = () => {
    if (!hasScrolledToEnd) {
      setHasScrolledToEnd(true);
      addInteraction('scrolled_to_end');
    }
  };




  const handleManualComplete = () => {
    if (content.isRequired && !minimumTimeReached) {
      toast.error(`Please spend at least ${Math.ceil(minimumTime / 60)} minutes viewing this content.`);
      return;
    }
    
    // For videos, check if fully watched
    if (content.type === 'video') {
      if (!videoCompleted) {
        toast.error('Please watch the complete video before finishing.');
        return;
      }
      toast.success('Video completed successfully!');
    }
    
    // For interactive exercises, check if answer is correct
    if (content.type === 'interactive') {
      if (interactiveAnswerCorrect === null) {
        toast.error('Please run your code and complete the exercise before finishing.');
        return;
      }
      if (interactiveAnswerCorrect === false) {
        toast.error('Your solution is incorrect. Please review the hints and try again.');
        return;
      }
      toast.success('Exercise completed successfully!');
    }
    
    // For subject interactive exercises, check if answer is correct
    if (content.type?.includes('-interactive') || content.interactiveData?.subjectType) {
      if (interactiveAnswerCorrect === null) {
        toast.error('Please complete the interactive exercise before finishing.');
        return;
      }
      if (interactiveAnswerCorrect === false) {
        toast.error('Your solution is incorrect. Please review the hints and try again.');
        return;
      }
      toast.success('Interactive exercise completed successfully!');
    }
    
    // For other content types
    if (content.type === 'text' || content.type === 'document') {
      toast.success('Content completed successfully!');
    }
    
    addInteraction('manually_completed');
    onComplete();
  };

  const canMarkComplete = () => {
    if (isCompleted) return false;
    if (!content.isRequired) return true;
    
    // Base requirements
    const baseRequirements = minimumTimeReached && (content.type !== 'text' || hasScrolledToEnd);
    
    // For videos, also require full completion
    if (content.type === 'video') {
      return baseRequirements && videoCompleted;
    }
    
    // For interactive exercises, also require correct answer
    if (content.type === 'interactive') {
      return baseRequirements && interactiveAnswerCorrect === true;
    }
    
    // For subject interactive exercises, also require correct answer
    if (content.type?.includes('-interactive') || content.interactiveData?.subjectType) {
      return baseRequirements && interactiveAnswerCorrect === true;
    }
    
    return baseRequirements;
  };

  const getContentIcon = () => {
    switch (content.type) {
      case 'video':
        return <PlayIcon className="h-5 w-5" />;
      case 'text':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'interactive':
        return <BeakerIcon className="h-5 w-5" />;
      case 'finance-interactive':
      case 'arts-interactive':
      case 'science-interactive':
      case 'social-interactive':
      case 'engineering-interactive':
      case 'health-interactive':
      case 'law-interactive':
      case 'education-interactive':
      case 'design-interactive':
      case 'communication-interactive':
      case 'math-interactive':
        return <BeakerIcon className="h-5 w-5" />;
      case 'document':
        return <BookOpenIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Convert markdown-style text to HTML with proper formatting
  const formatTextToHTML = (text: string) => {
    if (!text) return '';
    
    return text
      // Convert headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-gray-800 mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold text-gray-800 mt-5 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-gray-900 mt-6 mb-4">$1</h1>')
      // Convert bold text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      // Convert italic text
      .replace(/\*(.*?)\*/g, '<em class="italic text-gray-700">$1</em>')
      // Convert bullet points
      .replace(/^- (.*$)/gim, '<li class="ml-4 mb-1">• $1</li>')
      .replace(/^• (.*$)/gim, '<li class="ml-4 mb-1">• $1</li>')
      // Wrap consecutive list items in ul tags
      .replace(/(<li.*<\/li>)/g, '<ul class="mb-3">$1</ul>')
      .replace(/<\/ul>\s*<ul[^>]*>/g, '')
      // Convert numbered lists
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
      // Convert line breaks
      .replace(/\n\n/g, '</p><p class="mb-3 text-gray-700 leading-relaxed">')
      .replace(/\n/g, '<br/>')
      // Wrap in paragraph tags
      .replace(/^(.)/gm, '<p class="mb-3 text-gray-700 leading-relaxed">$1')
      .replace(/(.*)$/gm, '$1</p>')
      // Clean up multiple paragraph tags
      .replace(/<\/p>\s*<p[^>]*>/g, '</p><p class="mb-3 text-gray-700 leading-relaxed">')
      .replace(/<p[^>]*>\s*<\/p>/g, '');
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Content Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getContentIcon()}
            <div className="ml-3">
              <h2 className="text-2xl font-bold text-gray-900">{content.title}</h2>
              <p className="text-gray-600 mt-1">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-sm text-gray-500">
              <ClockIcon className="h-4 w-4 mr-1" />
              <span>
                {formatTime(timeSpent)} / {
                  content.type === 'video' && actualVideoDuration !== null 
                    ? `${actualVideoDuration} min (actual)`
                    : `${typeof content.duration === 'string' ? parseInt(content.duration) || 0 : (content.duration || 0)} min`
                }
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <EyeIcon className="h-4 w-4 mr-1" />
              <span>Viewing time</span>
            </div>
            {canMarkComplete() && (
              <button
                onClick={handleManualComplete}
                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                Finished
              </button>
            )}
            {isCompleted && (
              <div className="flex items-center text-green-600">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">Completed</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Progress Indicators */}
        {!isCompleted && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Progress Requirements:</span>
              <span className="text-gray-500">
                {minimumTimeReached ? '✓' : '○'} Minimum time ({Math.ceil(minimumTime / 60)}min)
                {content.type === 'text' && (
                  <span className="ml-2">
                    {hasScrolledToEnd ? '✓' : '○'} Read to end
                  </span>
                )}
                {content.type === 'video' && (
                  <span className="ml-2">
                    {videoCompleted ? '✓' : '○'} Watch complete
                  </span>
                )}
                {content.type === 'interactive' && (
                  <span className="ml-2">
                    {interactiveAnswerCorrect === true ? '✓' : 
                     interactiveAnswerCorrect === false ? '✗' : '○'} Correct answer
                  </span>
                )}
                {(content.type?.includes('-interactive') || content.interactiveData?.subjectType) && (
                  <span className="ml-2">
                    {interactiveAnswerCorrect === true ? '✓' : 
                     interactiveAnswerCorrect === false ? '✗' : '○'} Complete exercise
                  </span>
                )}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Content Body */}
      <div className="p-6">
        {content.type === 'video' && (
          <div className="aspect-video bg-gray-900 rounded-lg mb-6 relative overflow-hidden">
            {content.metadata?.videoUrl ? (
              // Real video player when video URL is available
              <video
                className="w-full h-full object-contain"
                controls
                preload="metadata"
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
                onPlay={() => {
                  setVideoPlaying(true);
                  const hasPlayedBefore = viewingSession?.interactions.some(i => i.includes('video_play_clicked'));
                  addInteraction(hasPlayedBefore ? 'video_resumed' : 'video_play_clicked');
                }}
                onPause={() => {
                  setVideoPlaying(false);
                  addInteraction('video_paused');
                }}
                onEnded={() => {
                  setVideoCompleted(true);
                  addInteraction('video_completed');
                }}
                onLoadedMetadata={(e) => {
                  const video = e.target as HTMLVideoElement;
                  // Store actual video duration in minutes
                  const actualDurationMinutes = video.duration / 60;
                  setActualVideoDuration(actualDurationMinutes);
                  console.log(`Video actual duration: ${actualDurationMinutes.toFixed(2)} minutes`);
                }}
                onTimeUpdate={(e) => {
                  const video = e.target as HTMLVideoElement;
                  const actualVideoDuration = video.duration;
                  const currentTime = video.currentTime;
                  
                  // Update time spent based on current video time
                  setTimeSpent(Math.floor(currentTime));
                  
                  // Check if video is 92% complete
                  if (currentTime >= actualVideoDuration * 0.92 && !videoCompleted) {
                    setVideoCompleted(true);
                    addInteraction('video_completed');
                  }
                }}
                onSeeking={(e) => {
                  const video = e.target as HTMLVideoElement;
                  const actualVideoDuration = video.duration;
                  const currentTime = video.currentTime;
                  const watchedPercentage = currentTime / actualVideoDuration;
                  
                  // Prevent seeking beyond watched progress unless 92% completed
                  if (!videoCompleted && watchedPercentage > (timeSpent / actualVideoDuration) + 0.05) {
                    video.currentTime = Math.min(timeSpent, actualVideoDuration * 0.92);
                    addInteraction('seek_prevented');
                    toast.error('Please watch the video without skipping ahead');
                  }
                }}
              >
                <source src={content.metadata.videoUrl} type="video/mp4" />
                <source src={content.metadata.videoUrl} type="video/webm" />
                <source src={content.metadata.videoUrl} type="video/ogg" />
                Your browser does not support the video tag.
              </video>
            ) : !videoPlaying ? (
              // Video Thumbnail/Preview for placeholder
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center text-white">
                  <PlayIcon className="h-16 w-16 mx-auto mb-4" />
                  <p className="text-lg font-medium">Video: {content.title}</p>
                  <p className="text-sm text-gray-300 mb-4">Duration: {typeof content.duration === 'string' ? parseInt(content.duration) || 0 : (content.duration || 0)} minutes</p>
                  <p className="text-sm text-red-300 mb-4">Video not rendered yet</p>
                  <button
                    onClick={() => {
                      setVideoPlaying(true);
                      // Check if this is initial play or resume
                      const hasPlayedBefore = viewingSession?.interactions.some(i => i.includes('video_play_clicked'));
                      addInteraction(hasPlayedBefore ? 'video_resumed' : 'video_play_clicked');
                    }}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center mx-auto"
                    disabled
                  >
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Video Not Playing
                  </button>
                </div>
              </div>
            ) : (
              // Simulated player for placeholder content
              <div 
                className="w-full h-full bg-black relative cursor-pointer"
              >
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="w-0 h-0 border-l-6 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent ml-1"></div>
                    </div>
                    <p className="text-lg font-medium">{content.title}</p>
                    <p className="text-sm text-gray-300">Playback (no video playing)</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {content.type === 'text' && (content.textContent || content.content) && (
          <div 
            className="prose max-w-none max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4"
            onScroll={(e) => {
              const element = e.target as HTMLElement;
              if (element.scrollTop + element.clientHeight >= element.scrollHeight - 10) {
                handleScrollToEnd();
              }
              // Track scroll interaction
              addInteraction('text_scrolled');
            }}
          >
            <div 
              dangerouslySetInnerHTML={{ 
                __html: formatTextToHTML(content.textContent || content.content || '')
              }} 
            />
          </div>
        )}

        {(content.type === 'interactive' ||
          content.type === 'coding-interactive' ||
          content.type === 'finance-interactive' || 
          content.type === 'arts-humanities-interactive' ||
          content.type === 'natural-sciences-interactive' ||
          content.type === 'social-sciences-interactive' ||
          content.type === 'engineering-tech-interactive' ||
          content.type === 'health-medical-interactive' ||
          content.type === 'law-interactive' ||
          content.type === 'education-interactive' ||
          content.type === 'communication-interactive' ||
          content.type === 'math-interactive' ||
          content.type === 'general-interactive' ||
          content.interactiveData?.subjectType) && content.interactiveData && (
          <SubjectInteractiveRouter 
            content={content}
            onComplete={(completed: boolean) => {
              if (completed) {
                addInteraction('solution_correct');
                setInteractiveAnswerCorrect(true);
              } else {
                addInteraction('solution_incorrect');
                setInteractiveAnswerCorrect(false);
              }
            }}
          />
        )}

        {content.type === 'document' && (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Document Resource</h3>
            <p className="text-gray-600 mb-4">{content.title}</p>
            <button 
              onClick={() => addInteraction('document_download_clicked')}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Download Document
            </button>
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      <div className="p-6 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <button
            onClick={onPrevious}
            disabled={!canNavigatePrevious}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              canNavigatePrevious
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Previous
          </button>

          <div className="text-sm text-gray-600">
            {content.type === 'video' && '📹'}
            {content.type === 'text' && '📄'}
            {content.type === 'interactive' && '⚙️'}
            {content.type === 'finance-interactive' && '💰'}
            {content.type === 'arts-interactive' && '🎨'}
            {content.type === 'science-interactive' && '🔬'}
            {content.type === 'social-interactive' && '📊'}
            {content.type === 'engineering-interactive' && '⚙️'}
            {content.type === 'health-interactive' && '🏥'}
            {content.type === 'law-interactive' && '⚖️'}
            {content.type === 'education-interactive' && '👩‍🏫'}
            {content.type === 'design-interactive' && '🎭'}
            {content.type === 'communication-interactive' && '💬'}
            {content.type === 'math-interactive' && '🧮'}
            {content.type === 'document' && '📎'}
            <span className="ml-2">{content.title}</span>
          </div>

          <button
            onClick={onNext}
            disabled={!canNavigateNext}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              canNavigateNext
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Next
            <ArrowRightIcon className="h-4 w-4 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentViewer;